import {createRouter, createWebHistory, RouteRecordRaw, RouterView} from "vue-router";
import useStore from "@/stores";
import {h} from "vue";
import {useEntryAttributionStore} from "@/stores/session/entry-attribution";
import CheckinFlowTrackingService from "@/services/checkin-flow-tracking-service";
import {canUseLaundry} from "@/services/laundry-access-service";

const Shell = {
  name: "Shell",
  render: () => h(RouterView)
};

const LAUNDRY_QR_ENTRY_POINT = "laundry_qr";

const routes: Array<RouteRecordRaw> = [
  // Admin area
  {
    path: "/reservation-home",
    component: Shell,
    meta: { audience: "admin", breadcrumbIgnore: true },
    children: [
      { path: "", name: "reservation-home", component: () => import("@/views/ReservationHomeView.vue"), meta: { crumb: "reservation-home" } },
      {
        path: "extend-reservation/:reservationId?",
        name: "extend-reservation",
        component: () => import("@/components/extend/ExtendReservationDialog.vue"),
        props: (route) => ({
          reservationId: (route.params.reservationId as string) || (route.query.reservationId as string) || ""
        }),
        meta: { crumb: "extend-reservation" }
      },
      { path: "offers", name: "offers", component: () => import("@/views/offers/OfferListDialog.vue"), props: true, meta: { crumb: "offers" } },
      { path: "offers/options", name: "offer-options", component: () => import("@/views/offers/OfferOptions.vue"), props: true, meta: { crumb: "options" } },
      { path: "offers/book", name: "offer-booker", component: () => import("@/views/offers/OfferBooker.vue"), props: true, meta: { crumb: "book" } },
      { path: "pay-overview", name: "pay-overview", component: () => import("@/components/PayReservationOverview.vue"), props: true, meta: { crumb: "pay-overview" } },
      { path: "payment-done", name: "reservation-home-confirmation", component: () => import("@/views/manage-reservation/ReservationPaymentDone.vue"), props: true, meta: { crumb: "payment-done" } }
    ]
  },

  // Guest area
  {
    path: "/guest",
    component: Shell,
    meta: { audience: "guest" },
    children: [
      {
        path: "",
        redirect: () => {
          const { session } = useStore();

          return session.isLoggedIn
            ? { name: "home" }
            : { name: "services" };
        }
      },
      { path: "home", name: "home", component: () => import("@/views/HomeView.vue"), meta: { crumb: "home", softHydrate: true } },
      {
        path: "services",
        name: "services",
        component: () => import("@/views/ServicesView.vue"),
        meta: {
          crumb: "services",
          public: true,
          guestPublic: true
          }
        },
      { path: "profile", name: "profile", component: () => import("@/views/ProfileView.vue"),
        meta: {
          requiresAuth: true,
          guestOnly: true
        }
      },
      {
        path: "/support/chat",
        name: "support-chat",
        component: () => import("@/views/ChatView.vue"),
        meta: { requiresAuth: true }
      },
      // --- Domotics group ---
      {
        path: "domotics",
        name: "domotics-parent",
        component: Shell,
        meta: {
          crumb: "domotics",
          crumbTo: "domotics",
          breadcrumbIgnore: true
        },
        children: [
          { path: "", name: "domotics", component: () => import("@/views/domotics/DomoticsView.vue"), meta: { crumb: "domotics" } },
          {
            path: "comfort",
            component: Shell,
            meta: { crumb: "comfort", crumbTo: "comfort" },
            children: [
              { path: "", name: "comfort", component: () => import("@/views/ComfortView.vue"), meta: { crumb: "comfort" } },
              { path: "statistics", name: "comfort-statistics", component: () => import("@/views/domotics/StatisticsView.vue"), meta: { crumb: "statistics" } }
            ]
          },
          { path: "thermostat", name: "thermostat", component: () => import("@/views/domotics/ThermostatView.vue"), meta: { crumb: "thermostat" } },
          { path: "devices", name: "devices", component: () => import("@/views/domotics/DevicesView.vue"), meta: { crumb: "devices" } }
        ]
      },
      // --- Reservation group in Guest section ---
      {
        path: "reservation",
        name: "reservation-parent",
        component: Shell,
        meta: { crumb: "reservation", crumbTo: "reservation" },
        children: [
          { path: "", name: "reservation", redirect: { name: "home" }, meta: { crumb: "reservation", breadcrumbIgnore: true } },
          { path: "checkin", name: "checkin", component: () => import("@/views/manage-reservation/CheckinView.vue"), meta: { crumb: "checkin" } },
          { path: "options", name: "reservation-options", component: () => import("@/views/manage-reservation/ReservationOptions.vue"), meta: { crumb: "options" } },
          { path: "overview", name: "reservation-overview", component: () => import("@/views/manage-reservation/ReservationOverview.vue"), meta: { crumb: "overview" } },
          { path: "passport-scanner", name: "passport-scanner", component: () => import("@/views/manage-reservation/PassportScanner.vue"), meta: { crumb: "passport-scanner" } },
          { path: "payment-done", name: "reservation-confirmation", component: () => import("@/views/manage-reservation/ReservationPaymentDone.vue"), meta: { crumb: "payment-done" } }
        ]
      },

      { path: "laundry", name: "laundry", component: () => import("@/views/laundry/LaundryView.vue"), meta: { crumb: "laundry", requiresLaundry: true } },
      { path: "payment", name: "payment", component: () => import("@/views/payment/PaymentMethodView.vue"), meta: { crumb: "payment" } },
      { path: "laundry/done", name: "payment-done", component: () => import("@/views/payment/PaymentDone.vue"), meta: { crumb: "payment-done", softHydrate: true } }
    ]
  },

  // Public (ignored in breadcrumbs)
  {
    path: "/laundry",
    name: "laundry-entry",
    redirect: (to) => ({
      name: "laundry",
      query: {
        ...to.query,
        entry_point: typeof to.query.entry_point === "string" ? to.query.entry_point : LAUNDRY_QR_ENTRY_POINT
      }
    }),
    meta: { public: true, breadcrumbIgnore: true }
  },
  { path: "/auth", name: "auth", component: () => import("@/views/LoginTypeView.vue"), meta: { public: true, breadcrumbIgnore: true } },
  { path: "/unit-picker", name: "unit-picker", component: () => import("@/views/UnitPicker.vue"), meta: { public: true, breadcrumbIgnore: true } },
  { path: "/session-loader", name: "session-loader", component: () => import("@/views/SessionDataLoader.vue"), meta: { public: true, breadcrumbIgnore: true } },

  {
    path: "/:pathMatch(.*)*",
    name: "not-found",
    redirect: () => {
      const { session } = useStore();

      return session.isGuest
        ? { name: "home" }
        : session.isAdmin
          ? { name: "reservation-home" }
          : { name: "services" };
    },
    meta: { public: true, breadcrumbIgnore: true }
  }

];
const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes
});

const PUBLIC_ROUTES = [
  "auth",
  "login-guest",
  "login-reservation",
  "laundry-entry",
  "unit-picker",
  "session-loader",
  "services"
];


router.beforeEach(async (to, from, next) => {
  const { session } = useStore();
  const entryAttribution = useEntryAttributionStore();

  entryAttribution.captureFromRoute(to);
  session.setFromRoute(to);

  const hasPublicContextQuery =
    typeof to.query.building_id === "string" ||
    typeof to.query.entry_point === "string" ||
    typeof to.query.route_to === "string" ||
    typeof to.query.utm_source === "string" ||
    typeof to.query.utm_medium === "string" ||
    typeof to.query.utm_content === "string";

  if (hasPublicContextQuery) {
    const {
      building_id,
      entry_point,
      route_to,
      utm_source,
      utm_medium,
      utm_content,
      ...restQuery
    } = to.query;

    const target = entryAttribution.consumeRouteTo();

    return next({
      name: target === "assistant" ? "services" : String(to.name ?? "services"),
      query: restQuery,
      hash: to.hash,
      replace: true
    });
  }

  if (to.meta?.public || PUBLIC_ROUTES.includes(String(to.name))) {
    if (session.needsHydration) {
      void session.onReady();
    }

    return next();
  }

  // ensure hydration attempt happens on every protected nav
  void session.onReady();

  if ((session as any).needsHydration) {
    if (to.name !== "session-loader") {
      return next({ name: "session-loader", query: { redirect: to.fullPath } });
    }
    return next();
  }

  const isInitialLoad = from.matched.length === 0;
  if (to.meta?.softHydrate && isInitialLoad && session.shouldRefreshGuest()) {
    void session.refreshGuestSmart({ background: true });
  }

  if (!session.isLoggedIn) {
    return next({ name: "session-loader", query: { redirect: to.fullPath } });
  }

  const audience = to.matched.find(r => r.meta?.audience)?.meta.audience as "guest" | "admin" | undefined;
  if (audience === "guest" && !session.isGuest) return next({ name: "reservation-home" });
  if (audience === "admin" && !session.isAdmin) return next({ name: "home" });

  if (to.meta?.requiresLaundry && !canUseLaundry(session)) {
    return next({ name: "home" });
  }

  if (to.meta?.requiresLaundry && entryAttribution.hasPendingAttribution) {
    void session.refreshGuestSmart({ background: true });
  }

  next();
});

router.afterEach((to, from): void => {
  CheckinFlowTrackingService.trackRouteView(to, from);
});





export default router;
